'use client'

import { ext } from '@/utils/linkTarget'
import { useState, useEffect } from 'react'
import HeaderGap from '@/components/atoms/headerGap'
import Container from '@/components/templates/container'
import Layout from '@/components/templates/layout'
import TestimonialsSection from '@/components/organisms/testimonialsSection'
import Community from '@/components/organisms/community'
import titleCase from '@/utils/titleCase'
import {
  Hero, Intro, StaggeredGallery, Feature, CoachesBand, VideoSection, FaqSection,
  body, h2, solidBtn,
} from '@/components/organisms/sportPage'
import GymPricing from '@/components/organisms/sportPage/gymPricing'

const REZERV = 'https://mahimabali.rezerv.co'
const WHATSAPP = 'https://wa.me/6281337582672'

const label = 'font-chivoMono text-[11px] uppercase tracking-widest text-khaki'
const textLink = 'pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber'
const pill = (active) =>
  `shrink-0 min-h-[44px] px-5 py-2.5 border font-chivoMono text-[12px] uppercase tracking-widest whitespace-nowrap transition-colors duration-200 ${
    active ? 'bg-forest border-forest text-background' : 'border-khaki/40 text-umber hover:border-forest'
  }`

// ── Class schedule (live from Rezerv) ──────────────────────────────────────

function Schedule({ heading, description }) {
  const [classes, setClasses] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    fetch('/api/rezerv/today?days=7')
      .then((r) => r.json())
      .then((d) => setClasses(d.classes ?? []))
      .catch(() => setClasses([]))
  }, [])

  if (classes && classes.length === 0) return null

  const byDay = {}
  ;(classes ?? []).forEach((c) => {
    if (!byDay[c.date]) byDay[c.date] = []
    byDay[c.date].push(c)
  })
  const today = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const days = Object.entries(byDay)
  const safeTab = Math.min(activeTab, Math.max(days.length - 1, 0))
  const activeItems = days[safeTab]?.[1]
  const dayLabel = (date, items) => (date === today ? 'Today' : items[0].dateLabel.split(' ').slice(0, 2).join(' '))

  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section id="schedule" aria-labelledby="schedule-title" className="scroll-mt-[100px]">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div className="flex flex-col gap-5 lg:gap-6 max-w-[560px]">
            <span className={label}>Live from Rezerv</span>
            <h2 id="schedule-title" className={h2}>{titleCase(heading || 'Class schedule')}</h2>
            <p className={body}>
              {description || 'Group classes this week. Drop in for one class or combine it with an open-gym day.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6 shrink-0">
            <a href="https://mahimabali.rezerv.co" target="_blank" rel="noopener noreferrer" className={solidBtn}>Book via Rezerv</a>
            <a href={WHATSAPP} {...ext(WHATSAPP)} className={textLink}>Book via WhatsApp</a>
          </div>
        </div>

        {classes === null ? (
          <p className={`${label} py-6`}>Loading…</p>
        ) : (
          <>
            <div role="tablist" aria-label="Day" className="flex gap-2 overflow-x-auto scrollbar-hidden border-b border-dune pb-6 mb-8 lg:mb-10">
              {days.map(([date, items], i) => (
                <button key={date} type="button" role="tab" aria-selected={i === safeTab} onClick={() => setActiveTab(i)} className={pill(i === safeTab)}>
                  {dayLabel(date, items)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
              {activeItems?.map((cls) => (
                <div key={cls.id} className="flex flex-col gap-2 p-4 lg:p-5 border border-dune">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-ttDrugsTrial text-olive text-[22px] leading-none">{cls.time}</span>
                    <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: cls.color ?? '#857d66' }} />
                  </div>
                  <span className="font-ttDrugsTrial text-olive text-[17px] leading-snug">{cls.name}</span>
                  <span className="mt-auto pt-3 font-chivoMono text-[11px] uppercase tracking-widest text-khaki">
                    {[cls.instructor, cls.facility].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </Container>
  )
}

// ── Personal training (same layout as the content sections) ───────────────

const PT_HIGHLIGHTS = [
  'Strength & conditioning', 'Athletic performance', 'Weight loss & body composition',
  'Mobility & flexibility', 'Recovery & injury prevention', 'Personalised programming',
]

const toPersonalTrainingSection = (pt = {}) => {
  const highlights = pt.highlights?.length ? pt.highlights.map((h) => h.label) : PT_HIGHLIGHTS
  return {
    heading: pt.heading || 'Personal training',
    body: [
      pt.intro || "Work one-on-one with a dedicated trainer in Mahima's gym. Every session is built around your goal: strength, athletic performance or recovering smarter.",
      ...highlights.map((h) => `- ${h}`),
    ].join('\n\n'),
    ctaButton: { name: pt.ctaLabel || 'Book a session', link: pt.ctaLink || '/gym-ubud/coaching' },
    image: pt.photo,
  }
}

// ── Page ───────────────────────────────────────────────────────────────────

const GymUbudPage = ({ pageAPI, testimonials, gymPricing, residentPlans, footerAPI, settingAPI }) => {
  const hero = pageAPI?.hero || {}
  const sections = pageAPI?.sections || []

  return (
    <Layout settingAPI={settingAPI} footerAPI={footerAPI}>
      <HeaderGap />
      <Hero cover={pageAPI?.cover} hero={hero} cta={{ name: 'Join the gym', link: REZERV }} />
      <Intro intro={hero.intro} showPricingLink={!pageAPI?.hidePricing} />
      <StaggeredGallery photos={pageAPI?.photos} alt="The gym at Mahima, Sayan, Ubud" />

      <Feature section={toPersonalTrainingSection(pageAPI?.personalTraining || {})} imageLeft solid />

      {sections.map((s, i) => (
        <Feature key={i} section={s} imageLeft={i % 2 === 1} />
      ))}

      <Schedule heading={pageAPI?.scheduleHeading} description={pageAPI?.scheduleDescription} />

      {(pageAPI?.ctaHeading || pageAPI?.ctaDescription) && (
        <Container outerClassName="mt-[100px] lg:mt-[150px]">
          <div className="flex flex-col gap-6 max-w-[640px]">
            {pageAPI.ctaHeading && <h2 className={h2}>{titleCase(pageAPI.ctaHeading)}</h2>}
            {pageAPI.ctaDescription && <p className={body}>{pageAPI.ctaDescription}</p>}
            <a href="/book-join" className={solidBtn}>Join or drop in</a>
          </div>
        </Container>
      )}

      {!pageAPI?.hidePricing && <GymPricing gym={gymPricing?.gym} plans={residentPlans} />}

      <CoachesBand coaches={pageAPI?.coaches} heading={pageAPI?.coachesHeading} />

      <div id="studio" />
      <VideoSection videoSection={pageAPI?.videoSection} />

      <div id="testimonials" className="mt-[100px] lg:mt-[150px]" />
      <TestimonialsSection testimonials={testimonials} googleReviewUrl={settingAPI?.googleReviewUrl}
        reviewCtaText={settingAPI?.reviewCtaText} heading={pageAPI?.testimonialsHeading} band />

      <FaqSection faq={pageAPI?.faq} />

      <Community title={settingAPI?.community?.title} description={settingAPI?.community?.description}
        image={settingAPI?.community?.image} ctaButton={settingAPI?.community?.ctaButton} />
    </Layout>
  )
}

export default GymUbudPage
